import React, { useState } from 'react';
import FileUploader from '../components/FileUploader';
import ProcessingIndicator from '../components/ProcessingIndicator';
import { compressImage } from '../utils/imageUtils';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import './ToolPage.css';

const ImageCompress = () => {
    const [files, setFiles] = useState([]);
    const [processing, setProcessing] = useState(false);
    const [quality, setQuality] = useState(0.8);
    const [processedFiles, setProcessedFiles] = useState([]);

    const handleFilesSelected = (selectedFiles) => {
        setFiles(selectedFiles);
        setProcessedFiles([]);
    };

    const handleCompress = async () => {
        if (files.length === 0) return;

        setProcessing(true);
        const compressed = [];

        try {
            for (const file of files) {
                const compressedFile = await compressImage(file, quality);
                compressed.push({
                    original: file,
                    compressed: compressedFile,
                    originalSize: file.size,
                    compressedSize: compressedFile.size,
                    reduction: ((1 - compressedFile.size / file.size) * 100).toFixed(1)
                });
            }
            setProcessedFiles(compressed);
        } catch (error) {
            alert('Compression failed: ' + error.message);
        } finally {
            setProcessing(false);
        }
    };

    const handleDownload = async () => {
        if (processedFiles.length === 1) {
            saveAs(processedFiles[0].compressed, processedFiles[0].compressed.name);
        } else {
            const zip = new JSZip();
            processedFiles.forEach((item, index) => {
                zip.file(item.compressed.name, item.compressed);
            });
            const content = await zip.generateAsync({ type: 'blob' });
            saveAs(content, 'compressed_images.zip');
        }
    };

    const formatSize = (bytes) => {
        return (bytes / 1024).toFixed(2) + ' KB';
    };

    return (
        <div className="tool-page">
            <div className="tool-header">
                <h1>📦 Image Compress</h1>
                <p>Compress images online, reduce file size, supports batch processing</p>
            </div>

            <div className="tool-content">
                <FileUploader
                    accept="image/*"
                    onFilesSelected={handleFilesSelected}
                    title="Upload Images"
                    description="Supports JPG, PNG, WebP and other formats"
                />

                {files.length > 0 && !processing && processedFiles.length === 0 && (
                    <div className="tool-controls">
                        <div className="control-group">
                            <label>Compression Quality: {Math.round(quality * 100)}%</label>
                            <input
                                type="range"
                                min="0.1"
                                max="1"
                                step="0.1"
                                value={quality}
                                onChange={(e) => setQuality(parseFloat(e.target.value))}
                                className="quality-slider"
                            />
                            <div className="quality-labels">
                                <span>Low Quality</span>
                                <span>High Quality</span>
                            </div>
                        </div>
                        <button className="btn btn-primary" onClick={handleCompress}>
                            Start Compression
                        </button>
                    </div>
                )}

                {processing && <ProcessingIndicator message="Compressing images..." />}

                {processedFiles.length > 0 && (
                    <div className="results">
                        <h3>Compression Complete!</h3>
                        <div className="results-list">
                            {processedFiles.map((item, index) => (
                                <div key={index} className="result-item">
                                    <span className="file-name">{item.original.name}</span>
                                    <div className="size-comparison">
                                        <span className="original-size">{formatSize(item.originalSize)}</span>
                                        <span className="arrow">→</span>
                                        <span className="compressed-size">{formatSize(item.compressedSize)}</span>
                                        <span className="reduction">Reduced {item.reduction}%</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <button className="btn btn-primary" onClick={handleDownload}>
                            {processedFiles.length === 1 ? 'Download' : 'Download All'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ImageCompress;
